import React, { useState } from 'react'
import { Button, Input, Select, DatePicker } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import moment from 'moment'
import styled from 'styled-components'
import AutoCompleteFilter from 'src/components/AutoComplete'
import PurchaseOrderInput, {
  GuidanceText,
} from 'src/components/invoices/PurchaseOrderInput'
import { CN_DOCUMENT_COPY } from 'src/utils/creditNoteDocumentHelpers'
import {
  UPLOAD_DRAFT_STATUS,
  UPLOAD_QUEUE_COPY,
} from 'src/utils/invoiceUploadQueueHelpers'
import {
  canRemoveUploadReviewFocus,
  isUploadReviewFocusEditable,
} from 'src/utils/invoiceUploadReviewHelpers'
import { isSparseUploadOcrFile } from 'src/utils/invoiceUploadSameInvoicePages'
import {
  getDocumentTypeCheckAmberCopy,
  shouldShowDocumentTypeCheckAmber,
} from 'src/utils/invoiceUploadQueuePolish'
import {
  canCreateSupplierFromUpload,
  CREATE_SUPPLIER_FROM_UPLOAD_COPY,
  getAiFieldBadgeLabel,
  getCreateSupplierFromUploadTitle,
  getInvoiceTotalEstimateFromLines,
  getMissingMandatoryUploadLabels,
  getOmsSupplierSuggestionsForUpload,
  getUploadSoftWarnings,
  getVenueSuppliersPath,
  INVOICE_TOTAL_HELPER_TEXT,
  canUseInvoiceTotalEstimate,
  isLikelyDeliveryNoteUpload,
  isNonOmsSupplierUpload,
  normaliseInvoiceDateInput,
} from 'src/utils/invoiceUploadHelpers'

const { Option } = Select

const PREVIEW_ZOOM_MIN = 1
const PREVIEW_ZOOM_MAX = 4
const PREVIEW_ZOOM_STEP = 0.25

const TypeCheckAmber = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  font-size: 13px;
  color: #ad6800;
  line-height: 1.45;
`

const TypeCheckLabel = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #d46b08;
  background: #fff1b8;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  padding: 1px 6px;
  line-height: 1.4;
`

const CnTeachDismiss = styled.button`
  margin-top: 6px;
  border: none;
  background: transparent;
  color: #8c6d1f;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  text-decoration: underline;
`

function clampPreviewZoom(value) {
  return Math.min(
    PREVIEW_ZOOM_MAX,
    Math.max(PREVIEW_ZOOM_MIN, Math.round(value * 100) / 100),
  )
}

function FieldBadge({ label }) {
  if (!label) return null
  return (
    <AiBadge
      data-edited={label === 'Edited' ? 'true' : undefined}
      data-non-oms={label === 'Non-OMS supplier' ? 'true' : undefined}
    >
      {label}
    </AiBadge>
  )
}

/**
 * Single-file focus card for Review uploads (Chunk U3).
 */
function ReviewUploadFocusCard({
  file,
  draft,
  venueId,
  venueSuppliers = [],
  previewZoom = 1,
  onPreviewZoomChange,
  onUpdateSupplier,
  onCreateSupplierFromUpload,
  onUpdateField,
  onUpdateTotal,
  onUseEstimate,
  onUpdatePurchaseOrder,
  onRemove,
  onRetryOcr,
  actionsDisabled = false,
  showDocumentTypeAmber = false,
  showCnTeach = false,
  onDismissCnTeach,
}) {
  const [creatingSupplier, setCreatingSupplier] = useState(false)

  if (!file || !draft) {
    return (
      <EmptyFocus>
        <EmptyTitle>{UPLOAD_QUEUE_COPY.allDoneTitle}</EmptyTitle>
        <EmptyBody>{UPLOAD_QUEUE_COPY.allDoneBody}</EmptyBody>
      </EmptyFocus>
    )
  }

  const editable = isUploadReviewFocusEditable(draft.status)
  const createSupplierTitle = getCreateSupplierFromUploadTitle(file)
  const showCreateSupplier =
    editable &&
    canCreateSupplierFromUpload(file) &&
    typeof onCreateSupplierFromUpload === 'function'

  const handleCreateSupplier = async () => {
    if (!showCreateSupplier || creatingSupplier) return
    setCreatingSupplier(true)
    try {
      await onCreateSupplierFromUpload(createSupplierTitle)
    } finally {
      setCreatingSupplier(false)
    }
  }
  const isReading =
    draft.status === UPLOAD_DRAFT_STATUS.QUEUED ||
    draft.status === UPLOAD_DRAFT_STATUS.READING
  const isFailed = draft.status === UPLOAD_DRAFT_STATUS.FAILED
  const sparseOcr =
    !isReading &&
    !isFailed &&
    draft.status === UPLOAD_DRAFT_STATUS.READY &&
    isSparseUploadOcrFile(file)
  const previewSrc = file.previewUrl || file.image?.base64 || ''
  const isPdf =
    String(previewSrc).startsWith('data:application/pdf;base64,') ||
    (Boolean(file.image?.isRemoteUrl) &&
      /\.pdf($|\?)/i.test(String(file.filename || previewSrc))) ||
    /\.pdf($|\?)/i.test(String(file.filename || ''))
  const missingLabels = getMissingMandatoryUploadLabels(file)
  const softWarnings = getUploadSoftWarnings(file)
  const deliveryNoteUpload = isLikelyDeliveryNoteUpload(file)
  const invoiceDateValue = file.invoiceDate
    ? moment(file.invoiceDate, 'YYYY-MM-DD')
    : null
  const zoom = clampPreviewZoom(previewZoom)
  const canRemove = canRemoveUploadReviewFocus(draft) && !actionsDisabled
  const isCn = file.documentType === 'creditNote'
  const documentTypeAmber =
    showDocumentTypeAmber || shouldShowDocumentTypeCheckAmber(file)

  return (
    <FocusRoot>
      {(isReading || isFailed || sparseOcr) && (
        <OcrFileStatus>
          {isReading && (
            <span>
              {draft.status === UPLOAD_DRAFT_STATUS.QUEUED
                ? 'Waiting to read…'
                : 'Reading the invoice…'}
            </span>
          )}
          {isFailed && !isReading && (
            <>
              <span>
                {file.ocrError || draft.error || UPLOAD_QUEUE_COPY.ocrFailed}
              </span>
              <Button
                size='small'
                type='link'
                onClick={onRetryOcr}
                disabled={actionsDisabled}
              >
                {UPLOAD_QUEUE_COPY.ocrRetry}
              </Button>
            </>
          )}
          {sparseOcr && !isReading && !isFailed && (
            <span>{UPLOAD_QUEUE_COPY.ocrSparse}</span>
          )}
        </OcrFileStatus>
      )}

      <FilePreview>
        <PreviewToolbar>
          {!isPdf && (
            <ZoomControls>
              <ZoomButton
                type='default'
                size='small'
                disabled={zoom <= PREVIEW_ZOOM_MIN}
                onClick={() =>
                  onPreviewZoomChange &&
                  onPreviewZoomChange(
                    clampPreviewZoom(zoom - PREVIEW_ZOOM_STEP),
                  )
                }
                aria-label='Zoom out'
              >
                −
              </ZoomButton>
              <ZoomLevel>{Math.round(zoom * 100)}%</ZoomLevel>
              <ZoomButton
                type='default'
                size='small'
                disabled={zoom >= PREVIEW_ZOOM_MAX}
                onClick={() =>
                  onPreviewZoomChange &&
                  onPreviewZoomChange(
                    clampPreviewZoom(zoom + PREVIEW_ZOOM_STEP),
                  )
                }
                aria-label='Zoom in'
              >
                +
              </ZoomButton>
              {zoom > PREVIEW_ZOOM_MIN && (
                <ZoomResetButton
                  type='link'
                  size='small'
                  onClick={() =>
                    onPreviewZoomChange && onPreviewZoomChange(PREVIEW_ZOOM_MIN)
                  }
                >
                  Reset
                </ZoomResetButton>
              )}
            </ZoomControls>
          )}
          {isPdf && previewSrc && (
            <OpenPdfLink
              href={previewSrc}
              target='_blank'
              rel='noopener noreferrer'
            >
              Open PDF in new tab
            </OpenPdfLink>
          )}
          <CrossIcon
            onClick={() => {
              if (!canRemove) return
              onRemove && onRemove()
            }}
            style={{
              opacity: canRemove ? 1 : 0.4,
              pointerEvents: canRemove ? 'auto' : 'none',
            }}
            title={UPLOAD_QUEUE_COPY.removeThis}
          >
            <CloseOutlined style={{ fontSize: '15px', color: '#fff' }} />
          </CrossIcon>
        </PreviewToolbar>
        {isPdf ? (
          previewSrc ? (
            <PdfPreviewFrame
              title={file.filename || 'Document PDF'}
              src={previewSrc}
            />
          ) : (
            <PreviewPlaceholder>
              PDF preview unavailable — open after confirm from the library.
            </PreviewPlaceholder>
          )
        ) : previewSrc ? (
          <PreviewImageWrap>
            <PreviewImage
              src={previewSrc}
              alt={file.filename || 'Upload preview'}
              style={{ transform: `scale(${zoom})` }}
            />
          </PreviewImageWrap>
        ) : (
          <PreviewPlaceholder>
            Preview unavailable for this incomplete upload — details below are
            still editable.
          </PreviewPlaceholder>
        )}
      </FilePreview>

      <FieldsBlock data-disabled={editable ? undefined : 'true'}>
        <FormField>
          <FieldLabel>
            <span>
              Supplier <RequiredMark>*</RequiredMark>
            </span>
            <FieldBadge label={getAiFieldBadgeLabel(file, 'supplier')} />
          </FieldLabel>
          <AutoCompleteFilter
            data={venueSuppliers}
            placeholder='Select supplier'
            onSelect={(id, title) =>
              onUpdateSupplier && onUpdateSupplier(id, title)
            }
            custom={file.supplierTitle || ''}
          />
          {(() => {
            const omsSuggestions = getOmsSupplierSuggestionsForUpload(
              file,
              venueSuppliers,
            ).filter(
              suggestion =>
                suggestion.id && suggestion.id !== String(file.supplier || ''),
            )
            if (!omsSuggestions.length || !editable) return null
            return (
              <OmsSuggestionBlock>
                <OmsSuggestionLabel>Closest OMS suppliers</OmsSuggestionLabel>
                <OmsSuggestionRow>
                  {omsSuggestions.map(suggestion => (
                    <OmsSuggestionButton
                      key={suggestion.id}
                      type='default'
                      size='small'
                      onClick={() =>
                        onUpdateSupplier &&
                        onUpdateSupplier(suggestion.id, suggestion.title)
                      }
                    >
                      Use {suggestion.title}
                    </OmsSuggestionButton>
                  ))}
                </OmsSuggestionRow>
              </OmsSuggestionBlock>
            )
          })()}
          {isNonOmsSupplierUpload(file) ? (
            <NonOmsNote>
              “{String(file.supplierTitle || '').trim() || 'This supplier'}”
              isn’t on Your Suppliers for this venue. Kept as Non-OMS — pick the
              correct OMS supplier above, or add them under{' '}
              <Link to={getVenueSuppliersPath(venueId)}>Your Suppliers</Link>.
              We won’t silently link a different supplier.{' '}
              {CREATE_SUPPLIER_FROM_UPLOAD_COPY.noteSuffix}
              {showCreateSupplier ? (
                <CreateSupplierRow>
                  <Button
                    type='primary'
                    ghost
                    size='small'
                    icon={<PlusOutlined />}
                    loading={creatingSupplier}
                    disabled={actionsDisabled || creatingSupplier}
                    onClick={handleCreateSupplier}
                  >
                    {creatingSupplier
                      ? CREATE_SUPPLIER_FROM_UPLOAD_COPY.creatingLabel
                      : CREATE_SUPPLIER_FROM_UPLOAD_COPY.buttonLabel(
                          createSupplierTitle,
                        )}
                  </Button>
                </CreateSupplierRow>
              ) : null}
            </NonOmsNote>
          ) : (
            <GuidanceText>
              Prefer an OMS supplier when you can — that unlocks automatic
              matching.
            </GuidanceText>
          )}
        </FormField>

        <FormField>
          <FieldLabel>
            <span>Document type</span>
            <FieldBadge label={getAiFieldBadgeLabel(file, 'documentType')} />
          </FieldLabel>
          <Select
            size='large'
            disabled={!editable}
            value={file.documentType || 'invoice'}
            onChange={value =>
              onUpdateField && onUpdateField('documentType', value)
            }
          >
            <Option value='invoice'>Invoice</Option>
            <Option value='creditNote'>Credit Note</Option>
          </Select>
          <GuidanceText>
            {isCn
              ? CN_DOCUMENT_COPY.uploadConfirm
              : 'Tagging as Credit Note helps process it correctly.'}
          </GuidanceText>
          {documentTypeAmber && (
            <TypeCheckAmber>
              <TypeCheckLabel>
                {UPLOAD_QUEUE_COPY.checkDocumentTypeAmberLabel}
              </TypeCheckLabel>
              <span>
                {getDocumentTypeCheckAmberCopy(file) ||
                  UPLOAD_QUEUE_COPY.checkDocumentType}
              </span>
            </TypeCheckAmber>
          )}
          {showCnTeach && (
            <CnTeaching>
              <div>{UPLOAD_QUEUE_COPY.cnInQueueHint}</div>
              {typeof onDismissCnTeach === 'function' ? (
                <CnTeachDismiss type='button' onClick={onDismissCnTeach}>
                  Got it
                </CnTeachDismiss>
              ) : null}
            </CnTeaching>
          )}
        </FormField>

        <FormField>
          <FieldLabel>
            <span>
              PO number <OptionalMark> optional</OptionalMark>
            </span>
            <FieldBadge
              label={getAiFieldBadgeLabel(file, 'purchaseOrderNumber')}
            />
          </FieldLabel>
          <PurchaseOrderInput
            venueId={venueId}
            value={file.purchaseOrderNumber || ''}
            supplierId={file.supplier}
            placeholder={
              isCn
                ? 'OMS PO (pick when applying)'
                : 'PO number (or leave blank)'
            }
            onChange={poUpdate =>
              onUpdatePurchaseOrder && onUpdatePurchaseOrder(poUpdate)
            }
          />
          <GuidanceText>
            {isCn
              ? 'Leave blank if unsure — pick the OMS purchase order when you Apply to PO. Do not use “With Reference To” invoice numbers as the PO.'
              : 'If ordered in OMS, enter the PO. If ordered outside OMS (e.g. Farmfoods), leave blank — Confirm, then use Create order from invoice.'}
          </GuidanceText>
        </FormField>

        <FormField>
          <FieldLabel>
            <span>
              Invoice / credit note number <RequiredMark>*</RequiredMark>
            </span>
            <FieldBadge label={getAiFieldBadgeLabel(file, 'invoiceNumber')} />
          </FieldLabel>
          <Input
            size='large'
            disabled={!editable}
            placeholder='Supplier invoice number'
            value={file.invoiceNumber || ''}
            onChange={event =>
              onUpdateField &&
              onUpdateField('invoiceNumber', event.target.value)
            }
          />
        </FormField>

        <FormField>
          <FieldLabel>
            <span>
              Total (including VAT)
              {!deliveryNoteUpload && (
                <>
                  {' '}
                  <RequiredMark>*</RequiredMark>
                </>
              )}
            </span>
            <FieldBadge label={getAiFieldBadgeLabel(file, 'invoiceTotal')} />
          </FieldLabel>
          <Input
            size='large'
            disabled={!editable}
            placeholder={
              deliveryNoteUpload ? 'Optional for delivery notes' : 'e.g. 51.95'
            }
            value={file.invoiceTotalInput || ''}
            onChange={event =>
              onUpdateTotal && onUpdateTotal(event.target.value)
            }
          />
          {editable &&
            canUseInvoiceTotalEstimate(file) &&
            (() => {
              const estimate = getInvoiceTotalEstimateFromLines(file)
              return (
                <EstimateOffer>
                  <EstimateText>
                    No total was printed. We added up the{' '}
                    {estimate.count > 0
                      ? `${estimate.count} line ${
                          estimate.count === 1 ? 'item' : 'items'
                        }`
                      : 'line items'}{' '}
                    to an estimated <strong>£{estimate.formatted}</strong>.
                  </EstimateText>
                  <EstimateButton
                    type='primary'
                    size='small'
                    onClick={onUseEstimate}
                  >
                    Use £{estimate.formatted}
                  </EstimateButton>
                </EstimateOffer>
              )
            })()}
          <GuidanceText>
            {deliveryNoteUpload
              ? 'Delivery notes often have no total — you can leave this blank.'
              : INVOICE_TOTAL_HELPER_TEXT}
          </GuidanceText>
        </FormField>

        <FormField>
          <FieldLabel>
            <span>
              Invoice date <RequiredMark>*</RequiredMark>
            </span>
            <FieldBadge label={getAiFieldBadgeLabel(file, 'invoiceDate')} />
          </FieldLabel>
          <DatePicker
            size='large'
            disabled={!editable}
            format='DD/MM/YYYY'
            style={{ width: '100%' }}
            value={invoiceDateValue}
            onChange={value =>
              onUpdateField &&
              onUpdateField('invoiceDate', normaliseInvoiceDateInput(value))
            }
          />
        </FormField>

        <FormField>
          <FieldLabel>Notes</FieldLabel>
          <Input
            size='large'
            disabled={!editable}
            placeholder='Notes (optional)'
            value={file.comment || ''}
            onChange={event =>
              onUpdateField && onUpdateField('comment', event.target.value)
            }
          />
        </FormField>

        {Array.isArray(softWarnings) && softWarnings.length > 0 && (
          <SoftWarnings>
            {softWarnings.map(warning => (
              <li key={warning.code || warning.message}>{warning.message}</li>
            ))}
          </SoftWarnings>
        )}
        {missingLabels.length > 0 && editable && (
          <MissingFieldsText>
            Still needed: {missingLabels.join(', ')}
          </MissingFieldsText>
        )}
      </FieldsBlock>

      <FocusActions>
        <Button onClick={onRemove} disabled={!canRemove}>
          {UPLOAD_QUEUE_COPY.removeThis}
        </Button>
      </FocusActions>
    </FocusRoot>
  )
}

export default ReviewUploadFocusCard

const FocusRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  flex: 1;
`

const EmptyFocus = styled.div`
  padding: 48px 24px;
  text-align: center;
`

const EmptyTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
`

const EmptyBody = styled.p`
  margin: 0;
  color: rgba(0, 0, 0, 0.65);
`

const OcrFileStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f5f5f5;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
`

const FilePreview = styled.div`
  position: relative;
  width: 100%;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
`

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: #f5f5f5;
  border-bottom: 1px solid #f0f0f0;
`

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ZoomButton = styled(Button)``
const ZoomResetButton = styled(Button)``
const ZoomLevel = styled.span`
  font-size: 12px;
  min-width: 40px;
  text-align: center;
`

const OpenPdfLink = styled.a`
  font-size: 13px;
`

const CrossIcon = styled.button`
  margin-left: auto;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const PdfPreviewFrame = styled.iframe`
  width: 100%;
  height: 220px;
  border: none;
  background: #fff;
`

const PreviewImageWrap = styled.div`
  width: 100%;
  height: 220px;
  overflow: auto;
  background: #fff;
`

const PreviewImage = styled.img`
  display: block;
  max-width: 100%;
  transform-origin: top left;
`

const PreviewPlaceholder = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 13px;
  color: #8c8c8c;
  background: #fff;
`

const FieldsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  &[data-disabled='true'] {
    opacity: 0.55;
    pointer-events: none;
  }
`

const FormField = styled.div`
  width: 100%;

  .ant-select,
  .ant-input,
  .ant-calendar-picker,
  .ant-select-auto-complete,
  .ant-auto-complete {
    width: 100% !important;
  }
`

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`

const RequiredMark = styled.span`
  color: #cf1322;
`

const OptionalMark = styled.span`
  color: rgba(0, 0, 0, 0.45);
  font-weight: 400;
  font-size: 12px;
`

const AiBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #389e0d;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
  padding: 1px 6px;

  &[data-edited='true'] {
    color: #096dd9;
    background: #e6f7ff;
    border-color: #91d5ff;
  }

  &[data-non-oms='true'] {
    color: #d46b08;
    background: #fff7e6;
    border-color: #ffd591;
  }
`

const OmsSuggestionBlock = styled.div`
  margin-top: 8px;
`

const OmsSuggestionLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
`

const OmsSuggestionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const OmsSuggestionButton = styled(Button)``

const NonOmsNote = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.4;
`

const CreateSupplierRow = styled.div`
  margin-top: 10px;
`

const CnTeaching = styled.div`
  margin: 8px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  font-size: 13px;
  color: #614700;
  line-height: 1.45;
`

const EstimateOffer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
  padding: 8px 10px;
  background: #fafafa;
  border-radius: 6px;
`

const EstimateText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #595959;
  line-height: 1.4;
`

const EstimateButton = styled(Button)``

const MissingFieldsText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #cf1322;
`

const SoftWarnings = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #ad6800;
`

const FocusActions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
`
